import { useState, useEffect } from 'react';
import { useObserver } from 'mobx-react-lite';
import { useHistory, useRouteMatch, useLocation } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { hasIsolatedMenus } from 'shared/core/helpers/dispensaries';
import { isMed, isRec } from 'shared/helpers/dispensaries';

import { useDispensary } from 'src/hooks/use-dispensary';
import { useStream } from 'src/hooks/use-stream';
import { useUI } from 'src/hooks/use-ui';
import { useUser } from 'src/hooks/use-user';
import type { SectionLink, SectionLinksObject } from 'src/models/section-links';
import { useOrdersStore } from 'src/stores/orders';
import AdminPublicEnv from 'src/utils/admin-public-env';
import { isBackofficeHostedFeatureAvailable } from 'src/utils/backoffice-hosted-route-policy';

export function useDispensaryAdminLinks(): SectionLinksObject {
  const [backToMenuLink, setBackToMenuLink] = useState<string | null>('');
  const Stream = useStream();
  const dispensary = useDispensary();
  const User = useUser();
  const UI = useUI();
  const router = useHistory();
  const location = useLocation();
  const ordersStore = useOrdersStore();
  const orderCount = useObserver(() => ordersStore?.active?.orderCount) ?? 0;
  const unreadConversationCount = useObserver(() => Stream.unreadCount);
  const isolatedMenus = hasIsolatedMenus(dispensary);
  const dispensaryRouteMatch = useRouteMatch<{ id: string }>('/dispensaries/:id');
  const atDispensaryMenu = useRouteMatch<{ id: string }>('/dispensaries/:id/menu');
  const atEditProduct = useRouteMatch<{ id: string; productId: string }>('/dispensaries/:id/menu/edit/:productId');
  const atAddSpecial = useRouteMatch<{ id: string }>('/dispensaries/:id/promote/specials/add');
  const atEditSpecial = useRouteMatch<{ id: string; specialId: string }>(
    '/dispensaries/:id/promote/specials/edit/:specialId'
  );
  const atAddAndEditCollection = useRouteMatch<{ id: string }>(
    '/dispensaries/:id/collections/collections/:action/:collectionId?'
  );
  const atAddAndEditCustomCategory = useRouteMatch<{ id: string }>(
    '/dispensaries/:id/collections/custom-categories/:action/:collectionId?'
  );
  const atAddAndEditImageBanner = useRouteMatch<{ id: string }>(
    '/dispensaries/:id/customize/image-banner/:action/:bannerId?'
  );
  const flags = useFlags();
  const cloudPrintEnabled = flags['growth.ecomm.cloud-prnt-receipt-printing.rollout'] ?? false;
  const specialsRedesignEnabled = flags['ecomm.specials-redesign.rollout'] ?? false;
  const configurablePurchaseLimitsEnabled = flags['ecomm.purchase-limits.configurable-overrides.rollout'] ?? false;

  const {
    hasAdsPermissions,
    hasAnalyticsPermissions: canViewAnalytics,
    hasCustomersPermissions: canViewCustomers,
    hasMenuPermissions: canViewMenu,
    hasMessagingPermissions: canViewMessaging,
    hasOrdersPermissions: canViewOrders,
    hasPersonalizationPermissions,
    hasPromotePermissions: canViewPromote,
    hasSettingsPermissions: canViewSettings,
    hasUsersPermissions: canViewUsers,
  } = User;

  const isCollectionEditingOrAdding = atAddAndEditCollection?.isExact;
  const isCustomCategoryEditingOrAdding = atAddAndEditCustomCategory?.isExact;
  const isImageBannerEditingOrAdding = atAddAndEditImageBanner?.isExact;

  const getBacklink = (): string | null => {
    if (!isCollectionEditingOrAdding && !isCustomCategoryEditingOrAdding) {
      return null;
    }
    return isCustomCategoryEditingOrAdding ? `${baseTo}/collections/custom-categories` : `${baseTo}/collections`;
  };

  const getCustomizeBacklink = (): string | null => {
    if (!isImageBannerEditingOrAdding) {
      return null;
    }
    return `${baseTo}/customize/image-banner`;
  };

  useEffect(() => {
    if (atDispensaryMenu && location.search.length > 0) {
      // if currently at the menu path and query params have been set,
      // use the location pathname and search values to preserve the
      // most recent query params by setting backToMenuLink in state
      setBackToMenuLink(`${location.pathname}${location.search}`);
      return;
    }

    if (backToMenuLink?.length === 0) {
      // if the backToMenuLink is an empty string, set it to the default menu route
      const basePathToMenu = dispensaryRouteMatch?.url;
      setBackToMenuLink(basePathToMenu ? `${basePathToMenu}/menu` : null);
    }
  }, [location, backToMenuLink, dispensaryRouteMatch?.url, atDispensaryMenu]);

  if (!dispensaryRouteMatch) {
    return {
      links: [],
      redirectPath: '/redirect',
    };
  }

  if (dispensary.id && !User.hasAccessToDispensary(dispensary.id)) {
    return {
      links: [],
      redirectPath: '/redirect',
    };
  }

  const basePath = dispensaryRouteMatch.path;
  const baseTo = dispensaryRouteMatch.url;

  const hasSettingsAccess = User.isSuperAdmin || canViewSettings;

  const links: SectionLink[] = [
    {
      badge: orderCount === 0 ? '' : orderCount,
      filePath: './orders',
      icon: 'orders',
      key: 'orders',
      label: 'Orders',
      routePath: `${basePath}/orders`,
      to: `${baseTo}/orders`,
      subHeaders: [
        { to: `${baseTo}/orders/current`, label: 'Current', visible: true },
        { to: `${baseTo}/orders/past`, label: 'Past', visible: true },
        { to: `${baseTo}/orders/cancelled`, label: 'Cancelled', visible: true },
      ],
      visible: User.isSuperAdmin || canViewOrders,
    },
    {
      backlink: atEditProduct?.isExact ? backToMenuLink : null,
      filePath: './menu',
      icon: 'menu',
      key: 'menu',
      label: 'Menu',
      routePath: `${basePath}/menu`,
      subHeaders: flags['ecomm.admin.menu-v2.rollout']
        ? [
            {
              to: `${baseTo}/menu`,
              label: 'Manage Products',
              visible: !atEditProduct,
              exact: true,
            },
            {
              to: `${baseTo}/menu/sort`,
              label: 'Default Sort',
              visible: !atEditProduct,
            },
            {
              to: `${baseTo}/menu/featured`,
              label: 'Featured Order',
              visible: !atEditProduct,
            },
            {
              to: `${baseTo}/menu/brand`,
              label: 'Brand Order',
              visible: !atEditProduct,
            },
          ]
        : [
            {
              to: `${baseTo}/menu/rec`,
              label: 'Recreational',
              visible: isolatedMenus && isRec(dispensary) && !atEditProduct,
            },
            {
              to: `${baseTo}/menu/med`,
              label: 'Medical',
              visible: isolatedMenus && isMed(dispensary) && !atEditProduct,
            },
          ],
      to: `${baseTo}/menu`,
      visible: User.isSuperAdmin || canViewMenu,
    },
    {
      backlink: getBacklink(),
      filePath: './collections',
      icon: 'collections',
      key: 'collections',
      label: 'Collections',
      headerLabel: isCustomCategoryEditingOrAdding ? 'Custom Categories' : 'Collections',
      routePath: `${basePath}/collections`,
      to: `${baseTo}/collections`,
      visible: hasSettingsAccess,
      subHeaders: [
        {
          label: 'Collections',
          onClick: () => UI.publishCheck({ newRoute: `${baseTo}/collections/collections`, router }),
          to: `${baseTo}/collections/collections`,
          visible: hasSettingsAccess && !isCollectionEditingOrAdding && !isCustomCategoryEditingOrAdding,
        },
        {
          label: 'Custom Categories',
          onClick: () => UI.publishCheck({ newRoute: `${baseTo}/collections/custom-categories`, router }),
          to: `${baseTo}/collections/custom-categories`,
          visible: hasSettingsAccess && !isCollectionEditingOrAdding && !isCustomCategoryEditingOrAdding,
        },
      ],
    },
    {
      filePath: './customers',
      icon: 'customers',
      key: 'customers',
      label: 'Customers',
      routePath: `${basePath}/customers`,
      to: `${baseTo}/customers`,
      visible: User.isSuperAdmin || canViewCustomers,
    },
    {
      filePath: './analytics',
      icon: 'analytics',
      key: 'analytics',
      label: 'Analytics',
      routePath: `${basePath}/analytics`,
      subHeaders: [
        { label: 'Sales', pageTitle: 'Sales Overview', to: `${baseTo}/analytics/sales`, visible: true },
        { label: 'Products', pageTitle: 'Products', to: `${baseTo}/analytics/products`, visible: true },
        { label: 'Brands', pageTitle: 'Brands', to: `${baseTo}/analytics/brands`, visible: true },
        { label: 'Ecommerce', pageTitle: 'Ecommerce', to: `${baseTo}/analytics/ecommerce`, visible: true },
        { label: 'Marketing', pageTitle: 'Marketing', to: `${baseTo}/analytics/marketing`, visible: true },
        {
          label: 'Delivery Heat Map',
          pageTitle: 'Delivery Heat Map',
          to: `${baseTo}/analytics/heat-map`,
          visible: true,
        },
      ],
      to: `${baseTo}/analytics`,
      visible: User.isSuperAdmin || canViewAnalytics,
    },
    {
      filePath: './reporting',
      icon: 'reporting',
      key: 'reporting',
      label: 'Reporting',
      routePath: `${basePath}/reporting`,
      to: `${baseTo}/reporting`,
      visible: User.isSuperAdmin || (canViewOrders && canViewCustomers),
    },
    {
      backlink: atEditSpecial?.isExact || atAddSpecial?.isExact ? `${baseTo}/promote/specials` : null,
      filePath: './promote',
      icon: 'promote',
      key: 'promote',
      label: 'Promote',
      routePath: `${basePath}/promote`,
      subHeaders: [
        {
          label: 'Specials',
          to: `${baseTo}/promote/specials`,
          visible: !atAddSpecial?.isExact && !atEditSpecial?.isExact,
        },
        {
          label: 'Coupons',
          to: `${baseTo}/promote/coupons`,
          visible: !atAddSpecial?.isExact && !atEditSpecial?.isExact,
        },
        {
          label: 'Specials Page',
          to: `${baseTo}/promote/specials-page`,
          visible: specialsRedesignEnabled && !atAddSpecial?.isExact && !atEditSpecial?.isExact,
        },
      ],
      to: `${baseTo}/promote`,
      visible: User.isSuperAdmin || canViewPromote,
    },
    {
      badge: unreadConversationCount > 0 ? unreadConversationCount : '',
      filePath: './messaging',
      icon: 'messaging',
      key: 'messaging',
      label: 'Messaging',
      routePath: `${basePath}/messaging`,
      to: `${baseTo}/messaging`,
      visible: User.isSuperAdmin || canViewMessaging,
    },
    {
      backlink: getCustomizeBacklink(),
      filePath: './customize',
      icon: 'customize',
      key: 'customize',
      label: 'Customize',
      routePath: `${basePath}/customize`,
      to: `${baseTo}/customize`,
      visible: hasSettingsAccess,
    },
    {
      filePath: './settings',
      icon: 'settings',
      key: 'settings',
      label: 'Settings',
      routePath: `${basePath}/settings`,
      to: `${baseTo}/settings`,
      visible: true,
      subHeaders: [
        {
          label: 'Store Info',
          onClick: () => UI.publishCheck({ newRoute: `${baseTo}/settings/info`, router }),
          to: `${baseTo}/settings/info`,
          visible: hasSettingsAccess,
        },
        {
          label: 'Ordering',
          onClick: () => UI.publishCheck({ newRoute: `${baseTo}/settings/ordering`, router }),
          to: `${baseTo}/settings/ordering`,
          visible: hasSettingsAccess,
        },
        {
          label: 'Hours',
          onClick: () => UI.publishCheck({ newRoute: `${baseTo}/settings/hours`, router }),
          to: `${baseTo}/settings/hours`,
          visible: hasSettingsAccess,
        },
        {
          label: 'My Account',
          onClick: () => UI.publishCheck({ newRoute: `${baseTo}/settings/account`, router }),
          to: `${baseTo}/settings/account`,
          visible: !User.isSuperAdmin,
        },
        {
          label: 'Users',
          onClick: () => UI.publishCheck({ newRoute: `${baseTo}/settings/users`, router }),
          to: `${baseTo}/settings/users`,
          visible:
            (User.isSuperAdmin || canViewUsers) &&
            isBackofficeHostedFeatureAvailable('dispensary-users', AdminPublicEnv.isBackofficeHosted),
        },
        {
          label: 'Devices',
          onClick: () => UI.publishCheck({ newRoute: `${baseTo}/settings/devices`, router }),
          to: `${baseTo}/settings/devices`,
          visible: hasSettingsAccess,
        },
        {
          label: 'Printers',
          onClick: () => UI.publishCheck({ newRoute: `${baseTo}/settings/printers`, router }),
          to: `${baseTo}/settings/printers`,
          visible: hasSettingsAccess && cloudPrintEnabled,
        },
        {
          label: 'Taxes',
          onClick: () => UI.publishCheck({ newRoute: `${baseTo}/settings/taxes`, router }),
          to: `${baseTo}/settings/taxes`,
          visible: hasSettingsAccess,
        },
        {
          label: 'Integrations',
          onClick: () =>
            UI.publishCheck({
              newRoute: `${baseTo}/settings/integrations`,
              router,
            }),
          to: `${baseTo}/settings/integrations`,
          visible: hasSettingsAccess,
        },
        {
          label: 'Store Menu',
          onClick: () => UI.publishCheck({ newRoute: `${baseTo}/settings/store-menu`, router }),
          to: `${baseTo}/settings/store-menu`,
          visible: hasSettingsAccess,
        },
        {
          label: 'Checkout',
          onClick: () => UI.publishCheck({ newRoute: `${baseTo}/settings/checkout`, router }),
          to: `${baseTo}/settings/checkout`,
          visible: hasSettingsAccess,
        },
        {
          label: 'Purchase Limits',
          onClick: () => UI.publishCheck({ newRoute: `${baseTo}/settings/purchase-limits`, router }),
          to: `${baseTo}/settings/purchase-limits`,
          visible: hasSettingsAccess && configurablePurchaseLimitsEnabled,
        },
        {
          label: 'Advanced Options',
          onClick: () => UI.publishCheck({ newRoute: `${baseTo}/settings/options`, router }),
          to: `${baseTo}/settings/options`,
          visible: hasSettingsAccess,
        },
        {
          label: 'Ads / Personalization',
          onClick: () => UI.publishCheck({ newRoute: `${baseTo}/settings/ads`, router }),
          to: `${baseTo}/settings/ads`,
          visible: hasAdsPermissions || hasPersonalizationPermissions,
        },
        {
          label: 'Audit Log',
          onClick: () => UI.publishCheck({ newRoute: `${baseTo}/settings/audit-log`, router }),
          to: `${baseTo}/settings/audit-log`,
          visible: User.isSuperAdmin,
        },
        // {
        //   label: 'Dispensary API Keys',
        //   onClick: () => UI.publishCheck({ newRoute: `${baseTo}/settings/api-keys`, router }),
        //   superAdminOnly: true,
        //   to: `${baseTo}/settings/api-keys`,
        //   visible: User.isSuperAdmin,
        // },
        // {
        //   label: 'Enterprise API Keys',
        //   onClick: () => UI.publishCheck({ newRoute: `${baseTo}/settings/enterprise-api-keys`, router }),
        //   superAdminOnly: true,
        //   to: `${baseTo}/settings/enterprise-api-keys`,
        //   visible: User.isSuperAdmin,
        // },
      ],
    },
  ];

  const visibleLinks = links.filter((link) => link.visible);
  const redirectPath = visibleLinks.length > 0 ? visibleLinks[0].to : '/redirect';

  return { links: visibleLinks, redirectPath };
}
